import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { Upload } from "@mui/icons-material";
import { supabase } from "../lib/supabaseClient";

const PublishConsentDialog = ({ open, onClose, report, user, onSuccess }) => {
  const [reason, setReason] = useState("30_days_passed");
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!report || !user) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setEvidenceFile(file);
      setError(""); // Clear previous errors
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (reason === "company_permission" && !evidenceFile) {
      setError("Evidence is required when selecting company permission.");
      return;
    }
    setError("");
    setIsSubmitting(true);

    try {
      let evidencePath = null;
      // 1. Upload evidence if provided
      if (evidenceFile) {
        const fileExt = evidenceFile.name.split(".").pop();

        // --- THIS IS THE CORRECTED LINE ---
        const path = `${user.id}/report-${report.id}/evidence.${fileExt}`;
        console.log("Uploading evidence to path:", path);
        const { error: uploadError } = await supabase.storage
          .from("publication-evidence")
          .upload(path, evidenceFile, {
            contentType: evidenceFile.type,
          });
        if (uploadError) throw uploadError;
        evidencePath = path;
      }

      // 2. Update the issue_reports table
      const { error: dbError } = await supabase
        .from("issue_reports")
        .update({
          publication_status: "pending_review",
          publication_consent_at: new Date().toISOString(),
          publication_consent_reason: reason,
          publication_evidence_url: evidencePath,
        })
        .eq("id", report.id);

      if (dbError) throw dbError;
      else console.log("Database update successful");

      // 3. Success
      onSuccess(report.id); // Notify parent component to update state
      handleClose();
    } catch (e) {
      console.error("Submission error:", e);
      setError(`Failed to submit: ${e.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setReason("30_days_passed");
    setEvidenceFile(null);
    setError("");
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Request to Publish: {report.report_title}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Please select the reason for your publication request:
            </FormLabel>
            <RadioGroup
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <FormControlLabel
                value="30_days_passed"
                control={<Radio />}
                label="30 days have passed since the initial submission."
              />
              <FormControlLabel
                value="company_permission"
                control={<Radio />}
                label="The company has given me explicit permission to publish."
              />
            </RadioGroup>
          </FormControl>

          {reason === "company_permission" && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Please upload evidence of permission (e.g., screenshot of
                emails, chat logs).
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<Upload />}
              >
                Upload Evidence
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {evidenceFile && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Selected: {evidenceFile.name}
                </Typography>
              )}
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} />}
        >
          {isSubmitting ? "Submitting..." : "Submit Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishConsentDialog;
